import { Box, Stack, styled, Typography } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { ApprovalStats, APPROVAL_STATUS_OPTIONS } from "../data";
import CustomSimpleCard from "@/components/custom/custom-simple-card";
import CustomSearchInput from "@/components/custom/custom-search-input";
import CustomSelectInput from "@/components/custom/custom-select-input";
import { CustomButton } from "@/components/custom/custom-button";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import CustomPagination from "@/components/custom/custom-pagination";
import { DataTable } from "@/components/custom/data-table";
import { ApprovalColumns } from "./_components/approval-column";
import { useApprovals } from "@/hooks/use-approvals";
import { useApprovalStats } from "@/hooks/use-approval-stats";

const RootWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(3),
  margin: theme.spacing(3),
}));

const Card = styled(Box)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 12,
  padding: theme.spacing(2.5),
  backgroundColor: theme.palette.background.paper,
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
}));

const ApprovalManagement = () => {
  const [searchParams] = useSearchParams();
  const search = searchParams.get("search") || "";
  const status = searchParams.get("status");
  const page = Number(searchParams.get("page") || 0);
  const size = Number(searchParams.get("size") || 10);

  const queryParams = {
    page,
    size,
    search,
    status: status || undefined,
  };

  const {
    data: approvals,
    isLoading,
    isError,
  } = useApprovals(true, queryParams);
  const { stats, isLoading: statsLoading } = useApprovalStats();

  return (
    <RootWrapper>
      <Box>
        <Typography variant="h6" fontWeight={600}>
          Approval Management
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Review and manage system approval requests with full visibility and
          audit control.
        </Typography>
      </Box>

      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
        {ApprovalStats.map(({ title, objKeyRepr, icon }, idx) => (
          <CustomSimpleCard
            key={`${objKeyRepr}-${idx}`}
            title={title}
            icon={icon}
            isLoading={statsLoading}
          >
            <Typography variant="h6">{stats?.[objKeyRepr] ?? 0}</Typography>
          </CustomSimpleCard>
        ))}
      </Stack>

      <Card>
        <Stack
          direction={"row"}
          spacing={3}
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction={"row"} spacing={3}>
            <CustomSearchInput
              paramKey="search"
              placeholder="Search approvals..."
            />

            <CustomSelectInput
              paramKey="status"
              label="Status"
              placeholder="All Statuses"
              options={APPROVAL_STATUS_OPTIONS}
            />
          </Stack>

          <CustomButton
            variantType="outline"
            endIcon={<FileDownloadOutlinedIcon fontSize="small" />}
            onClick={() => {}}
            isLoading={false}
            sx={{ height: 35 }}
          >
            Export
          </CustomButton>
        </Stack>

        <DataTable
          data={approvals?.data ?? []}
          columns={ApprovalColumns}
          isLoading={isLoading}
          isError={isError}
          noDataMessage="No approval requests found."
        />
        <CustomPagination
          total={approvals?.totalCount ?? 0}
          perPageOptions={[5, 10, 20, 50, 100]}
        />
      </Card>
    </RootWrapper>
  );
};

export default ApprovalManagement;
